import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, Grid3X3, Percent, Route, Save, Tags, Upload } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Spinner } from "./common";
import { api, errMsg, downloadBlob } from "../lib/api";
import { MissingRatesBanner } from "./MissingRatesBanner";
import { ReadOnlyNotice } from "./FeatureGate";

/**
 * ARV Rate Matrix (Property Tax) — UP ARV method.
 * Matrix columns come straight from the Road Width master (labels only — no numeric
 * width is used in any calculation). Each column has C/R/M rate cells per
 * construction type + one Vacant Land rate.
 * Building ARV = Carpet Area x Rate x 12 · Vacant ARV = Vacant Area x Rate x 12.
 * Each configured tax-head percentage generates a demand line = ARV x %.
 */
export function ArvMatrixTab({ code, fyId, readOnly = false }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [vacant, setVacant] = useState({});
  const [pcts, setPcts] = useState({});
  const [classes, setClasses] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingMap, setSavingMap] = useState(false);
  const [importing, setImporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/t/${code}/rate-engine/arv-matrix`, { params: { financial_year_id: fyId } })
      .then((r) => {
        const d = r.data;
        setData(d);
        setRows(Object.fromEntries(d.construction_types.map((c) => [c.id, { ...(d.rows[c.id] || {}) }])));
        setVacant({ ...(d.vacant || {}) });
        setPcts(Object.fromEntries(d.tax_percents.map((p) => [p.tax_head_id, p.percent || ""])));
        setClasses(Object.fromEntries(d.usage_types.map((u) => [u.id, u.arv_class || u.effective_class])));
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [code, fyId]);
  useEffect(() => { if (fyId) load(); }, [load, fyId]);

  if (!data) return <Spinner label="Loading ARV matrix..." />;
  const setCell = (ctId, key, v) => setRows((r) => ({ ...r, [ctId]: { ...(r[ctId] || {}), [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/t/${code}/rate-engine/arv-matrix`, {
        financial_year_id: fyId,
        rows: data.construction_types.map((c) => ({ construction_type_id: c.id, rates: rows[c.id] || {} })),
        vacant,
        tax_percents: Object.entries(pcts).map(([tax_head_id, percent]) => ({
          tax_head_id, percent: Number(percent) || 0 })),
      });
      toast.success(`ARV matrix saved — ${r.data.rows} construction row(s) + Vacant Land + tax percentages`);
      setRefreshKey((k) => k + 1);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const saveMappings = async () => {
    setSavingMap(true);
    try {
      await api.put(`/t/${code}/rate-engine/arv-mappings`, {
        usage_classes: data.usage_types.map((u) => ({ id: u.id, arv_class: classes[u.id] || null })),
      });
      toast.success("Usage class mappings saved");
      setRefreshKey((k) => k + 1);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingMap(false); }
  };

  const downloadTemplate = async () => {
    try {
      await downloadBlob(`/t/${code}/rate-engine/arv-matrix/template?financial_year_id=${fyId}`,
        "arv-rate-matrix-template.xlsx");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/t/${code}/rate-engine/arv-matrix/import?financial_year_id=${fyId}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const s = r.data;
      toast.success(`Matrix imported: ${s.imported_rows} construction row(s), ${s.vacant_road_widths} vacant rate(s)` +
        (s.construction_types_created ? `, ${s.construction_types_created} construction type(s) created` : "") +
        (s.road_widths_created ? `, ${s.road_widths_created} road width(s) created` : ""));
      if (s.error_count > 0) toast.warning(`${s.error_count} cell(s) had errors — e.g. ${s.errors[0]}`);
      setRefreshKey((k) => k + 1);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const rws = data.road_widths || [];

  return (
    <div className="space-y-4">
      {readOnly && <ReadOnlyNotice what="the ARV rate matrix" testId="arv-readonly-notice" />}
      <MissingRatesBanner code={code} fyId={fyId} linkToRates={false} refreshKey={refreshKey} />

      {/* ---- Rate matrix ---- */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Grid3X3 className="h-4 w-4" /> ARV Rate Matrix (₹ per sqft)
              </CardTitle>
              <CardDescription data-testid="arv-formula-note">
                Columns come from your <span className="font-medium">Road Width master</span> (labels only — the width value plays no role).
                ARV = Carpet Area × Rate × 12 (buildings) · Vacant Area × Rate × 12 (vacant land). Tax demand = ARV × each annual tax percentage below.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="arv-template-button">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Template
              </Button>
              {!readOnly && <label>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={doImport}
                  data-testid="arv-import-input" />
                <Button variant="outline" size="sm" asChild disabled={importing}>
                  <span className="cursor-pointer" data-testid="arv-import-button">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {importing ? "Importing..." : "Import (Excel)"}
                  </span>
                </Button>
              </label>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <fieldset disabled={readOnly} className="space-y-4 min-w-0" data-testid="arv-matrix-fieldset">
          {rws.length === 0 ? (
            <div className="border border-dashed rounded-lg p-6 text-center text-sm text-muted-foreground" data-testid="arv-no-roadwidths">
              <Route className="h-6 w-6 mx-auto mb-2 opacity-50" />
              No road widths found. Add entries in <span className="font-medium">Master Data → Road Widths</span> —
              each entry becomes a rate column here, named exactly as per your municipality style.
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm" data-testid="arv-matrix-table">
                <thead>
                  <tr className="bg-primary text-primary-foreground">
                    <th rowSpan={2} className="text-left px-3 py-2 font-semibold border-r border-primary-foreground/20 min-w-[150px]">Construction Type</th>
                    {rws.map((w) => (
                      <th key={w.id} colSpan={3} className="px-2 py-1.5 font-semibold text-center border-r border-primary-foreground/20 last:border-r-0"
                        data-testid={`arv-col-${w.width_label}`}>
                        {w.width_label}
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-primary/90 text-primary-foreground">
                    {rws.map((w) => data.classes.map((c) => (
                      <th key={`${w.id}_${c}`} title={data.class_labels[c]}
                        className="px-2 py-1 text-center font-medium w-20 border-r border-primary-foreground/10 last:border-r-0">{c}</th>
                    )))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.construction_types.map((ct) => (
                    <tr key={ct.id} className="hover:bg-accent/40" data-testid={`arv-row-${ct.const_type_name}`}>
                      <td className="px-3 py-1.5 font-medium border-r">{ct.const_type_name}</td>
                      {rws.map((w) => data.classes.map((c) => {
                        const key = `${w.id}_${c}`;
                        return (
                          <td key={key} className="px-1 py-1 text-center border-r last:border-r-0">
                            <Input type="number" step="any" min="0" className="h-8 w-full min-w-[64px] text-center px-1"
                              value={rows[ct.id]?.[key] ?? ""}
                              onChange={(e) => setCell(ct.id, key, e.target.value)}
                              data-testid={`arv-cell-${ct.const_type_name}-${w.width_label}-${c}`} />
                          </td>
                        );
                      }))}
                    </tr>
                  ))}
                  <tr className="bg-muted/60" data-testid="arv-row-vacant">
                    <td className="px-3 py-1.5 font-semibold border-r">Vacant Land</td>
                    {rws.map((w) => (
                      <td key={w.id} colSpan={3} className="px-2 py-1 text-center border-r last:border-r-0">
                        <Input type="number" step="any" min="0" className="h-8 w-32 mx-auto text-center"
                          value={vacant[w.id] ?? ""}
                          onChange={(e) => setVacant((v) => ({ ...v, [w.id]: e.target.value }))}
                          data-testid={`arv-vacant-${w.width_label}`} />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* ---- Annual tax percentages ---- */}
          <div className="border rounded-lg p-3 space-y-2">
            <p className="text-sm font-semibold flex items-center gap-1.5">
              <Percent className="h-3.5 w-3.5" /> Annual tax rates (% of ARV)
            </p>
            <p className="text-xs text-muted-foreground">
              Each tax head with a percentage &gt; 0 creates its own demand line: amount = ARV × %.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="arv-tax-percents">
              {data.tax_percents.map((p) => (
                <div key={p.tax_head_id} className="space-y-1">
                  <Label className="text-xs">{p.tax_head_name}{p.tax_code ? ` (${p.tax_code})` : ""}</Label>
                  <div className="relative">
                    <Input type="number" step="any" min="0" max="100" className="h-9 pr-8"
                      value={pcts[p.tax_head_id] ?? ""}
                      onChange={(e) => setPcts((x) => ({ ...x, [p.tax_head_id]: e.target.value }))}
                      data-testid={`arv-pct-${p.tax_head_name}`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!readOnly && <Button onClick={save} disabled={saving || rws.length === 0} className="w-full sm:w-auto" data-testid="arv-save-button">
            <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving..." : "Save matrix & tax rates"}
          </Button>}
          </fieldset>
        </CardContent>
      </Card>

      {/* ---- Usage class mapping ---- */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading">Usage Class Mapping</CardTitle>
          <CardDescription>
            Link each Usage Type to a C/R/M class — the calculator picks the matrix column from the property's road width and the class from the floor's usage type.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Accordion type="multiple" data-testid="arv-mappings-accordion">
            <AccordionItem value="uc">
              <AccordionTrigger className="text-sm" data-testid="arv-usage-mapping-trigger">
                <span className="flex items-center gap-2"><Tags className="h-3.5 w-3.5" /> Usage Type → C / R / M</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
                  {data.usage_types.map((u) => (
                    <div key={u.id} className="flex items-center justify-between gap-2 border rounded-md px-2.5 py-1.5">
                      <span className="text-sm truncate" title={u.usage_type_name}>
                        {u.usage_type_name}
                        {!u.arv_class && <span className="text-muted-foreground text-[10px]"> (auto)</span>}
                      </span>
                      <Select value={classes[u.id] || u.effective_class} onValueChange={(v) => setClasses((x) => ({ ...x, [u.id]: v }))} disabled={readOnly}>
                        <SelectTrigger className="h-8 w-36 shrink-0" data-testid={`arv-class-select-${u.usage_type_name}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {data.classes.map((c) => <SelectItem key={c} value={c}>{c} — {data.class_labels[c]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          {!readOnly && <Button variant="outline" onClick={saveMappings} disabled={savingMap} data-testid="arv-save-mappings-button">
            <Save className="h-3.5 w-3.5 mr-1.5" /> {savingMap ? "Saving..." : "Save usage class mappings"}
          </Button>}
        </CardContent>
      </Card>
    </div>
  );
}
